import { useCallback, useEffect, useState } from "react";

interface PortalDataState<T> {
  key: string;
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function usePortalData<T>(loader: () => Promise<T>, key: string): PortalDataState<T> {
  const [reloadCounter, setReloadCounter] = useState(0);
  const [state, setState] = useState<Omit<PortalDataState<T>, "reload">>({
    key,
    data: null,
    loading: true,
    error: null,
  });
  const reload = useCallback(() => {
    setState((previous) => {
      if (previous.key !== key) return previous;
      return { ...previous, loading: true, error: null };
    });
    setReloadCounter((value) => value + 1);
  }, [key]);

  useEffect(() => {
    let cancelled = false;

    loader()
      .then((data) => {
        if (cancelled) return;
        setState({ key, data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          key,
          data: null,
          loading: false,
          error: error instanceof Error ? error : new Error("Unknown error"),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [key, loader, reloadCounter]);

  if (state.key !== key) {
    return { key, data: null, loading: true, error: null, reload };
  }

  return { ...state, reload };
}
